import { useCallback, useMemo } from 'react';
import { useLaborCategoryQuery } from '@access-cms/api-client';
import type { PdtScanStatus } from '@access-cms/shared-constants';

import { Drawer } from '../../../../components/Drawer';
import { UploadedFile } from '../../../../components/FileUpload';
import type { DropdownOption } from '../../../../components/Dropdown';
import { useAlerts } from '../../../../hooks';

import { EXISTING_PDT_FILE_ID, openLaborCategoryPdt } from '../../utils/labor-category-pdt';
import { LaborCategoryData, LaborCategoryModal, LaborCategoryModalMode } from './LaborCategoryModal';

interface LaborCategoryDrawerProps {
  contractId: string;
  contractNumber: string;
  'data-testid'?: string;
  heading: string;
  initialData?: LaborCategoryData;
  isOpen: boolean;
  isSubmitting?: boolean;
  laborCategoryId?: string;
  mode: LaborCategoryModalMode;
  riskLevelOptions: DropdownOption<string>[];
  onClose: () => void;
  onGetHelp?: () => void;
  onSave: (data: LaborCategoryData) => void;
}

export function LaborCategoryDrawer({
  contractId,
  contractNumber,
  'data-testid': dataTestId,
  heading,
  initialData,
  isOpen,
  isSubmitting = false,
  laborCategoryId,
  mode,
  riskLevelOptions,
  onClose,
  onGetHelp,
  onSave,
}: LaborCategoryDrawerProps) {
  const baseTestId = dataTestId ?? 'labor-category-drawer';
  const { errorAlert } = useAlerts();
  const shouldFetchLaborCategory = mode === LaborCategoryModalMode.EDIT && !!laborCategoryId;
  const laborCategoryQuery = useLaborCategoryQuery(contractId, laborCategoryId ?? '', {
    enabled: isOpen && shouldFetchLaborCategory,
  });

  const fetchedInitialData = useMemo((): LaborCategoryData | undefined => {
    const laborCategory = laborCategoryQuery.data;
    if (!laborCategory) {
      return undefined;
    }

    // Only show a prior PDT after upload was confirmed; reserved/failed uploads keep pdtFileName but not pdtUploadedAt
    const existingFiles: UploadedFile[] =
      laborCategory.pdtUploadedAt && laborCategory.pdtFileName
        ? [
            {
              documentId: laborCategory.id,
              file: new File([], laborCategory.pdtFileName, { type: 'application/pdf' }),
              id: EXISTING_PDT_FILE_ID,
              isExisting: true,
              name: laborCategory.pdtFileName,
              scanStatus: (laborCategory.pdtScanStatus as PdtScanStatus | null | undefined) ?? undefined,
              size: 0,
            },
          ]
        : [];

    return {
      files: existingFiles,
      name: laborCategory.categoryName ?? '',
      riskLevel: laborCategory.riskLevel ?? '',
    };
  }, [laborCategoryQuery.data]);

  const resolvedInitialData = initialData ?? fetchedInitialData;

  const handleFileClick = useCallback(
    async (file: UploadedFile) => {
      try {
        await openLaborCategoryPdt(file, {
          contractId,
          laborCategoryId: file.documentId ?? laborCategoryId,
        });
      } catch (error) {
        console.error('[PDT] Failed to open labor category PDT', {
          contractId,
          error: error instanceof Error ? error.message : String(error),
          laborCategoryId: file.documentId ?? laborCategoryId,
        });
        errorAlert({
          message: error instanceof Error ? error.message : 'The PDT file could not be opened. Please try again.',
          title: 'Unable to open PDT',
        });
      }
    },
    [contractId, errorAlert, laborCategoryId],
  );

  return (
    <Drawer
      data-testid={baseTestId}
      heading={heading}
      isOpen={isOpen}
      width='50%'
      onClose={onClose}>
      {shouldFetchLaborCategory && laborCategoryQuery.isLoading ? (
        <p data-testid={`${baseTestId}-loading`}>Loading labor category...</p>
      ) : null}
      <LaborCategoryModal
        key={isOpen ? `${mode}-${laborCategoryId ?? 'new'}` : 'closed'}
        contractId={contractId}
        contractNumber={contractNumber}
        initialData={resolvedInitialData}
        isSubmitting={isSubmitting}
        laborCategoryId={laborCategoryId}
        mode={mode}
        riskLevelOptions={riskLevelOptions}
        onCancel={onClose}
        onFileClick={handleFileClick}
        onGetHelp={onGetHelp}
        onSave={onSave}
      />
    </Drawer>
  );
}
